<template>
  <div class="dialog-overlay" @click.self="oth_showInsuAccount(false)">
    <div class="dialog-box">
      <!-- 헤더 -->
      <div class="dialog-header">
        <div class="header-left">
          <h2><i class="ph ph-key"></i> 보험사 계정</h2>
          <div class="filter-tabs">
            <button 
              v-for="tab in filterTabs" 
              :key="tab.value"
              :class="{ active: activeFilter === tab.value }"
              @click="activeFilter = tab.value"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="header-right">
          <div class="search-box">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="보험사 검색..."
            >
          </div>
          <button class="close-btn" @click="oth_showInsuAccount(false)">
            <i class="ph ph-x"></i>
          </button>
        </div>
      </div>

      <!-- 보험사 그리드 -->
      <div class="company-grid">
        <div 
          class="company-card"
          v-for="company in filteredCompanies"
          :key="company.idx"
          :class="{ 
            'has-account': getAccount(company.idx),
            'active': selectedCompany?.idx === company.idx 
          }"
          @click="selectCompany(company)"
        >
          <div class="card-logo">
            <img :src="company.short_img" :alt="company.name">
          </div>
          <span class="card-name">{{ company.name }}</span>
          <div class="card-badge" v-if="getAccount(company.idx)">
            <i class="ph ph-check-circle-fill"></i>
          </div>
          <div 
            class="password-age-badge" 
            v-if="getAccount(company.idx)"
            :class="getPasswordAgeClass(getAccount(company.idx).password_changed_at)"
          >
            {{ getPasswordAgeDays(getAccount(company.idx).password_changed_at) }}일
          </div>
        </div>

        <!-- 빈 상태 -->
        <div class="empty-grid" v-if="filteredCompanies.length === 0">
          <i class="ph ph-magnifying-glass"></i>
          <p>검색 결과가 없습니다</p>
        </div>
      </div>

      <!-- 하단 계정 편집 패널 -->
      <transition name="slide-up">
        <div class="edit-panel" v-if="selectedCompany">
          <div class="panel-header">
            <div class="panel-company">
              <img :src="selectedCompany.short_img" class="panel-logo">
              <div class="panel-info">
                <strong>{{ selectedCompany.name }}</strong>
                <span class="type-badge">{{ selectedCompany.type === 'life' ? '생명' : '손해' }}</span>
              </div>
            </div>
            <button class="panel-close" @click="selectedCompany = null">
              <i class="ph ph-x"></i>
            </button>
          </div>

          <div class="panel-form">
            <div class="form-row">
              <div class="form-group">
                <label>아이디 (FP 코드)</label>
                <div class="input-with-btn">
                  <input 
                    type="text" 
                    v-model="form.identify" 
                    placeholder="로그인 아이디"
                  >
                  <button class="copy-btn" @click="copyToClipboard(form.identify, '아이디')" title="복사">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>비밀번호</label>
                <div class="input-with-btn">
                  <input 
                    :type="showPassword ? 'text' : 'password'" 
                    v-model="form.password" 
                    placeholder="비밀번호"
                  >
                  <button class="toggle-btn" @click="showPassword = !showPassword">
                    <i class="ph" :class="showPassword ? 'ph-eye-slash' : 'ph-eye'"></i>
                  </button>
                  <button class="copy-btn" @click="copyToClipboard(form.password, '비밀번호')" title="복사">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>메모</label>
              <input 
                type="text"
                v-model="form.memo" 
                placeholder="예: 90일마다 변경 필요, OTP 사용"
              >
            </div>

            <!-- 비밀번호 변경일 정보 -->
            <div class="pw-info" v-if="currentAccount">
              <span>
                <i class="ph ph-clock"></i>
                비밀번호 변경 후 
                <strong :class="getPasswordAgeClass(currentAccount.password_changed_at)">
                  {{ getPasswordAgeDays(currentAccount.password_changed_at) }}일
                </strong>
                경과
              </span>
              <button class="reset-pw-date" @click="markPasswordChanged">
                <i class="ph ph-arrows-clockwise"></i> 오늘로 갱신
              </button>
            </div>
          </div>

          <div class="panel-actions">
            <button class="btn-delete" v-if="currentAccount" @click="deleteAccount">
              <i class="ph ph-trash"></i> 삭제
            </button>
            <button class="btn-save" @click="saveAccount">
              <i class="ph ph-floppy-disk"></i> {{ currentAccount ? '저장' : '등록' }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';

export default {
  name: 'InsuranceAccountDialog',
  props: {
    oth_showInsuAccount: { type: Function, required: true }
  },
  setup() {
    const getMbid = inject('getMbid');
    const sql_query = inject('sql_query');
    const insuCompanys = inject('insuCompanys');

    const accountList = ref([]);
    const selectedCompany = ref(null);
    const showPassword = ref(false);
    const searchQuery = ref('');
    const activeFilter = ref('all');

    const filterTabs = [
      { label: '전체', value: 'all' },
      { label: '생명', value: 'life' },
      { label: '손해', value: 'damage' }
    ];

    const form = ref({
      identify: '',
      password: '',
      memo: ''
    });

    // 필터링된 보험사 목록
    const filteredCompanies = computed(() => {
      if (!insuCompanys?.value) return [];
      
      return insuCompanys.value.filter(company => {
        // 타입 필터
        if (activeFilter.value !== 'all' && company.type !== activeFilter.value) {
          return false;
        }
        // 검색 필터
        if (searchQuery.value) {
          return company.name.toLowerCase().includes(searchQuery.value.toLowerCase());
        }
        return true;
      });
    });

    // 현재 선택된 보험사의 계정
    const currentAccount = computed(() => {
      if (!selectedCompany.value) return null;
      return getAccount(selectedCompany.value.idx);
    });

    // 계정 조회 함수
    function getAccount(companyIdx) {
      return accountList.value.find(a => a.company_idx == companyIdx);
    }

    // 계정 목록 조회
    async function fetchAccounts() {
      const sql = `
        SELECT a.*, c.name AS company_name, c.short_img
        FROM insurance_account a
        LEFT JOIN insu_company c ON c.idx = a.company_idx
        WHERE a.mb_id = '${getMbid()}'
      `;
      const result = await sql_query(sql);
      accountList.value = Array.isArray(result) ? result : [];
    }

    // 보험사 선택
    function selectCompany(company) {
      selectedCompany.value = company;
      showPassword.value = false;
      
      const account = getAccount(company.idx);
      if (account) {
        form.value = {
          identify: account.identify,
          password: account.password,
          memo: account.memo || ''
        };
      } else {
        form.value = { identify: '', password: '', memo: '' };
      }
    }

    // 계정 저장 (등록/수정)
    async function saveAccount() {
      if (!form.value.identify || !form.value.password) {
        alert('아이디와 비밀번호를 입력해주세요.');
        return;
      }

      const account = currentAccount.value;
      const escapedPassword = form.value.password.replace(/'/g, "\\'");
      const escapedMemo = form.value.memo ? `'${form.value.memo.replace(/'/g, "\\'")}'` : 'NULL';

      if (account) {
        // 수정
        const passwordChanged = form.value.password !== account.password;
        const sql = `
          UPDATE insurance_account SET
            identify = '${form.value.identify}',
            password = '${escapedPassword}',
            memo = ${escapedMemo}
            ${passwordChanged ? ', password_changed_at = NOW()' : ''}
          WHERE idx = ${account.idx}
        `;
        await sql_query(sql);
      } else {
        // 신규 등록
        const sql = `
          INSERT INTO insurance_account 
          (mb_id, company_idx, identify, password, memo, password_changed_at)
          VALUES (
            '${getMbid()}',
            ${selectedCompany.value.idx},
            '${form.value.identify}',
            '${escapedPassword}',
            ${escapedMemo},
            NOW()
          )
        `;
        await sql_query(sql);
      }

      await fetchAccounts();
      alert('저장되었습니다.');
    }

    // 계정 삭제
    async function deleteAccount() {
      if (!currentAccount.value) return;
      if (!confirm(`${selectedCompany.value.name} 계정을 삭제하시겠습니까?`)) return;

      await sql_query(`DELETE FROM insurance_account WHERE idx = ${currentAccount.value.idx}`);
      await fetchAccounts();
      form.value = { identify: '', password: '', memo: '' };
    }

    // 비밀번호 변경일 갱신
    async function markPasswordChanged() {
      if (!currentAccount.value) return;
      if (!confirm('비밀번호 변경일을 오늘로 갱신하시겠습니까?')) return;

      await sql_query(`
        UPDATE insurance_account 
        SET password_changed_at = NOW() 
        WHERE idx = ${currentAccount.value.idx}
      `);
      await fetchAccounts();
    }

    // 클립보드 복사
    function copyToClipboard(text, label) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      alert(`${label}가 복사되었습니다.`);
    }

    // 비밀번호 경과일 계산
    function getPasswordAgeDays(date) {
      if (!date) return '?';
      const diff = new Date() - new Date(date);
      return Math.floor(diff / (1000 * 60 * 60 * 24));
    }

    function getPasswordAgeClass(date) {
      const days = getPasswordAgeDays(date);
      if (days === '?') return 'unknown';
      if (days >= 90) return 'danger';
      if (days >= 60) return 'warning';
      return 'safe';
    }

    onMounted(() => {
      fetchAccounts();
    });

    return {
      accountList,
      selectedCompany,
      currentAccount,
      showPassword,
      searchQuery,
      activeFilter,
      filterTabs,
      filteredCompanies,
      form,
      getAccount,
      selectCompany,
      saveAccount,
      deleteAccount,
      markPasswordChanged,
      copyToClipboard,
      getPasswordAgeDays,
      getPasswordAgeClass
    };
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-surface-deep);
  backdrop-filter: blur(var(--effect-blur));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
}

.dialog-box {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-background-light);
}

/* 헤더 */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-background-light);
  gap: 16px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.dialog-header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-primary);
  white-space: nowrap;
}

.filter-tabs {
  display: flex;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  padding: 4px;
  gap: 4px;
}

.filter-tabs button {
  padding: 6px 14px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-tabs button.active {
  background: var(--color-brand);
  color: #000;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  transition: all 0.15s ease;
}

.search-box:focus-within {
  border-color: var(--color-brand);
}

.search-box i {
  color: var(--color-text-secondary);
  font-size: 16px;
}

.search-box input {
  border: none;
  background: transparent;
  outline: none;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  width: 140px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 4px;
  display: flex;
}

.close-btn:hover {
  color: var(--color-text-primary);
}

/* 그리드 */
.company-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 20px;
  overflow-y: auto;
  align-content: start;
}

.company-grid::-webkit-scrollbar {
  display: none;
}

.company-card {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  cursor: pointer;
  border: 2px dashed rgb(122, 122, 122);
  position: relative;
  opacity: 0.2;
}

.company-card.has-account {
  opacity: 1;
  border: 2px solid rgb(178, 220, 255);
}

.company-card:hover {
  background: var(--color-background-light);
  opacity: 1;
}

.company-card.active {
  border-color: var(--color-brand);
  background: var(--color-brand-light);
  opacity: 1;
}

.card-logo {
  width: 60%;
  height: auto;
  aspect-ratio:1/1;
  background: #fff;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.card-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.card-name {
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.2;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--color-brand);
  font-size: 18px;
}

.password-age-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
}

.password-age-badge.safe { background: #e8f5e9; color: #2e7d32; }
.password-age-badge.warning { background: #fff3e0; color: #ef6c00; }
.password-age-badge.danger { background: #ffebee; color: #c62828; }
.password-age-badge.unknown { background: var(--color-background-light); color: var(--color-text-secondary); }

.empty-grid {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
}

.empty-grid i {
  font-size: 48px;
  margin-bottom: 12px;
}

/* 하단 편집 패널 */
.edit-panel {
  background: var(--color-surface);
  border-top: 1px solid var(--color-background-light);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-company {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-logo {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  object-fit: contain;
  background: #fff;
}

.panel-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.panel-info strong {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
}

.type-badge {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.panel-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 4px;
}

.panel-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
}

.form-group input {
  padding: 10px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  background: var(--color-background);
  color: var(--color-text-primary);
}

.input-with-btn {
  display: flex;
  gap: 6px;
}

.input-with-btn input {
  flex: 1;
  min-width: 0;
}

.input-with-btn button {
  width: 38px;
  flex-shrink: 0;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
  cursor: pointer;
  font-size: 16px;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-with-btn button:hover {
  background: var(--color-background-light);
}

.pw-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--color-background);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.pw-info i {
  margin-right: 4px;
}

.pw-info strong.safe { color: #2e7d32; }
.pw-info strong.warning { color: #ef6c00; }
.pw-info strong.danger { color: #c62828; }

.reset-pw-date {
  background: none;
  border: none;
  font-size: var(--font-size-xs);
  color: var(--color-brand);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.reset-pw-date:hover {
  text-decoration: underline;
}

.panel-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.panel-actions button {
  padding: 10px 20px;
  border-radius: var(--radius-md);
  border: none;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.btn-delete {
  background: #ffebee;
  color: #c62828;
}

.btn-delete:hover {
  background: #ffcdd2;
}

.btn-save {
  background: var(--color-brand);
  color: #000;
}

.btn-save:hover {
  filter: brightness(1.1);
}

/* 애니메이션 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* 반응형 */
@media (max-width: 600px) {
  .dialog-box {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .dialog-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-left {
    width: 100%;
    justify-content: space-between;
  }

  .header-right {
    width: 100%;
  }

  .search-box {
    flex: 1;
  }

  .search-box input {
    width: 100%;
  }

  .company-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 16px;
  }

  .card-logo {
    width: 40px;
    height: 40px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .panel-actions {
    flex-direction: column;
  }

  .panel-actions button {
    justify-content: center;
  }
}
</style>